import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DayOfWeek, HostUser } from '@vendasta/meetings';
import { Observable, Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { DailyAvailability, TimeRange } from '../../interface';
import { SETTINGS_VIEW, POSTHOG_ACTIONS, POSTHOG_CATEGORIES, POSTHOG_KEYS } from '../../constants';
import { ScheduleSettingsViewService } from '../schedule-settings-view.service';
import { indexToDayOfWeekMapping, ScheduleSettingsService } from '../schedule-settings.service';
import { EditAvailabilityDialogComponent } from './edit-availability-dialog';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
// @ts-expect-error Angular 21 doesn't like imports past the base package
import { AvailabilityRuleList } from '@vendasta/meetings/lib';
import { getCommonTimeSlots } from '../../utils';
import { UnsavedChangesGuard } from '../../shared/unsaved-changes-guard.service';

@Component({
  selector: 'meeting-scheduler-settings-advanced-availability-card',
  templateUrl: './advanced-availability-card.component.html',
  styleUrls: ['./advanced-availability-card.component.scss'],
  standalone: false,
})
export class AdvancedAvailabilityCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() timezone: string;
  @Input() currentView: string;
  @Input() eventTypeId: string;
  @Input() selectedHosts: HostUser[];
  @Input() hostAvailability: Observable<Map<string, AvailabilityRuleList>>;
  @Input() isMultiHost: boolean;
  @Input() meetingDuration: number;
  @Input() displayAvailabilitySelectionError: boolean;
  @Input() displayAvailabilitySelectionSlotError: boolean;
  @Input() displayAvailabilityInsufficientDurationError: boolean;
  @Input() enableTracking: boolean;

  @Output() availabilitySaved: EventEmitter<void> = new EventEmitter<void>();
  @Output() dayUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() hasSelectedAvailability: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() selectedAvailability: EventEmitter<DailyAvailability> = new EventEmitter<DailyAvailability>();

  daysOfWeek = [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
    DayOfWeek.SUNDAY,
  ];

  loadingAvailabilityForm = true;
  savingAvailabilityForm = false;
  isAvailabilityFormOpened = false;
  isAvailabilitySet = false;
  form: UntypedFormGroup;

  timeOptions: Date[] = [];

  private readonly subscriptions: Subscription[] = [];
  private availability?: DailyAvailability;

  constructor(
    private readonly alertService: SnackbarService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly meetingSchedulerService: MeetingSchedulerStoreService,
    private readonly scheduledMeetingsService: ScheduleSettingsService,
    private readonly scheduleSettingsViewService: ScheduleSettingsViewService,
    private readonly unsavedChangesGuard: UnsavedChangesGuard,
    private readonly dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private readonly analyticsService: ProductAnalyticsService,
  ) {}

  ngOnInit(): void {
    if (this.currentView === SETTINGS_VIEW) {
      this.timeOptions = this.scheduledMeetingsService.timeOptions;
      this.setupAvailabilityForm();
    } else if (!this.eventTypeId) {
      this.setupAvailabilityForm();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventTypeId']?.currentValue && this.currentView !== SETTINGS_VIEW) {
      this.setupAvailabilityForm();
    }
  }

  private async setupAvailabilityForm(): Promise<void> {
    this.loadingAvailabilityForm = true;
    if (this.currentView == SETTINGS_VIEW) {
      this.availability = await this.scheduledMeetingsService.loadInitialAvailability();
    } else if (this.eventTypeId) {
      this.availability = await this.scheduledMeetingsService.loadInitialAvailability(this.eventTypeId);
    } else {
      const emptyDays = new Map<DayOfWeek, TimeRange[]>();
      this.daysOfWeek.forEach((day) => emptyDays.set(day, []));
      this.availability = { days: emptyDays };
    }

    this.selectedAvailability.emit(this.availability);
    const dayEnabled = new Map<DayOfWeek, boolean>();
    const ranges = this.availability.days;
    this.daysOfWeek.forEach((day) => dayEnabled.set(day, (ranges.get(day) || []).length > 0));

    this.isAvailabilitySet = Array.from(dayEnabled.values()).some((enabled) => enabled);
    this.hasSelectedAvailability.emit(this.isAvailabilitySet);

    this.form = this.formBuilder.group({
      dayEnabled: this.formBuilder.control(dayEnabled),
      ranges: this.formBuilder.control(ranges),
      timezone: this.formBuilder.control(this.timezone),
    });
    this.loadingAvailabilityForm = false;
    // Register forms to create a warning when leaving the page with changes in these forms
    this.unsavedChangesGuard.registerForms([this.form]);
  }

  setAvailability(): void {
    this.savingAvailabilityForm = true;
    const dayEnabled = this.form.controls.dayEnabled.value as Map<DayOfWeek, boolean>;
    const dayRanges = this.form.controls.ranges.value as Map<DayOfWeek, TimeRange[]>;
    const selectedTimeZone = this.form.controls.timezone.value;

    const availability: DailyAvailability = { days: new Map() };
    dayEnabled.forEach((available, day) => {
      if (available) {
        availability.days.set(day, dayRanges.get(day));
      }
    });
    const meetingTypeIdForSave = this.eventTypeId ?? undefined;
    this.subscriptions.push(
      this.scheduledMeetingsService
        .setAvailability(availability, selectedTimeZone, meetingTypeIdForSave)
        .pipe(
          finalize(() => {
            this.savingAvailabilityForm = false;
          }),
        )
        .subscribe({
          next: () => {
            this.selectedAvailability.emit(availability);
            this.scheduleSettingsViewService.resetStateStorage();
            this.alertService.openSuccessSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.AVAILABILITY_SAVED');
            this.form.markAsPristine(); // Mark the form as pristine so we don't get unnecessary unsaved changes warnings when navigating away
            if (this.enableTracking) {
              this.analyticsService.trackEvent(
                POSTHOG_KEYS.HOST_PREFERENCE_UPDATED,
                POSTHOG_CATEGORIES.USER,
                POSTHOG_ACTIONS.CLICK,
              );
            }
            this.availabilitySaved.emit();
          },
          error: () => {
            this.alertService.openErrorSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.AVAILABILITY_SAVED_ERROR');
          },
        }),
    );
  }

  saveWithChanges(changes: Map<DayOfWeek, TimeRange[]>): void {
    const dayEnabled = this.form.controls.dayEnabled.value as Map<DayOfWeek, boolean>;
    const ranges = this.form.controls.ranges.value as Map<DayOfWeek, TimeRange[]>;
    changes.forEach((newRanges, day) => {
      dayEnabled.set(day, newRanges.length > 0);
      ranges.set(day, newRanges);
    });
    this.setAvailability();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  isDayEnabled(day: DayOfWeek): boolean {
    return this.form.controls.dayEnabled.value.get(day);
  }

  setIsDayEnabled(day: DayOfWeek, enabled: boolean): void {
    this.form.controls.dayEnabled.value.set(day, enabled);
    this.form.controls.dayEnabled.markAsDirty(); // .set() does not mark the form as dirty automatically

    // Update isAvailabilitySet for settings view - check if any day is enabled AND has time ranges
    if (this.currentView === SETTINGS_VIEW) {
      const dayEnabled = this.form.controls.dayEnabled.value as Map<DayOfWeek, boolean>;
      const dayRanges = this.form.controls.ranges.value as Map<DayOfWeek, TimeRange[]>;
      this.isAvailabilitySet = Array.from(dayEnabled.entries()).some(
        ([day, enabled]) => enabled && (dayRanges.get(day) || []).length > 0,
      );
    }

    if (enabled && this.rangesForDay(day).length <= 0) {
      this.openEditAvailabilityDialog(day);
    }
    if (this.currentView !== SETTINGS_VIEW) {
      this.dayUpdated.emit(true);

      const dayEnabled = this.form.controls.dayEnabled.value as Map<DayOfWeek, boolean>;
      const dayRanges = this.form.controls.ranges.value as Map<DayOfWeek, TimeRange[]>;
      const availability: DailyAvailability = { days: new Map() };
      dayEnabled.forEach((available, day) => {
        if (available) {
          availability.days.set(day, dayRanges.get(day));
        }
      });
      const data: { timeRanges: Map<DayOfWeek, TimeRange[]>; availability: Map<DayOfWeek, boolean> } = {
        availability: dayEnabled,
        timeRanges: dayRanges,
      };
      this.isAvailabilitySet = Array.from(dayEnabled.values()).some((enabled) => enabled);
      this.hasSelectedAvailability.emit(this.isAvailabilitySet);
      this.selectedAvailability.emit(availability);
      this.meetingSchedulerService.updateSchedule(data);
    }
  }

  rangesForDay(day: DayOfWeek): TimeRange[] {
    const ranges = this.form.controls.ranges.value as Map<DayOfWeek, TimeRange[]>;
    return ranges.get(day) || [];
  }

  openEditAvailabilityDialog(day: DayOfWeek): void {
    this.isAvailabilityFormOpened = true;
    if (this.savingAvailabilityForm) {
      return;
    }

    let hostAvailabilitySlots: Map<string, AvailabilityRuleList> = new Map();
    if (this.hostAvailability && typeof this.hostAvailability.pipe === 'function') {
      this.hostAvailability.pipe(take(1)).subscribe((availability) => {
        hostAvailabilitySlots = availability;
      });
    }

    // Format time to "h:mm AM/PM"
    const formatTime = (time: { hours: number; minutes: number }) => {
      const hours12 = time.hours % 12 || 12;
      const minutes = time.minutes.toString().padStart(2, '0');
      const ampm = time.hours < 12 || time.hours === 24 ? 'AM' : 'PM';
      return `${hours12}:${minutes} ${ampm}`;
    };

    // Calculate common available slots for selected hosts
    const commonRanges = this.isMultiHost
      ? getCommonTimeSlots(
          day,
          hostAvailabilitySlots,
          this.selectedHosts.map((m) => m.userId),
        )
      : [];

    const commonSlots = commonRanges
      .map((slot) => `${formatTime(slot.from)} - ${formatTime(slot.to)}`)
      .sort((a, b) => {
        // Extract start time from each slot string
        const getStartMinutes = (slotStr: string) => {
          const [start] = slotStr.split(' - ');
          const [time, ampm] = start.split(' ');
          let hours = time.split(':').map(Number)[0];
          const minutes = time.split(':').map(Number)[1];
          if (ampm === 'PM' && hours !== 12) hours += 12;
          if (ampm === 'AM' && hours === 12) hours = 0;
          return hours * 60 + minutes;
        };
        return getStartMinutes(a) - getStartMinutes(b);
      });

    const dialogRef = this.dialog.open(EditAvailabilityDialogComponent, {
      data: {
        ranges: this.rangesForDay(day),
        day,
        saveCallback: (changes: Map<DayOfWeek, TimeRange[]>) => this.decideFlow(changes),
        isMultiHost: this.isMultiHost,
        availableSlots: commonSlots,
      },
    });
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe(() => {
        const hasAnyRanges = this.rangesForDay(day).length > 0;
        if (!hasAnyRanges) {
          this.setIsDayEnabled(day, false);
        }
        this.cdr.detectChanges();
      }),
    );
  }

  decideFlow(changes) {
    if (this.currentView == SETTINGS_VIEW) {
      this.saveWithChanges(changes);
    } else {
      const dayEnabled = this.form.controls.dayEnabled.value as Map<DayOfWeek, boolean>;
      const ranges = this.form.controls.ranges.value as Map<DayOfWeek, TimeRange[]>;

      this.isAvailabilitySet = Array.from(dayEnabled.values()).some((enabled) => enabled);
      this.hasSelectedAvailability.emit(this.isAvailabilitySet);
      changes.forEach((newRanges, day) => {
        dayEnabled.set(day, newRanges.length > 0);
        ranges.set(day, newRanges);
      });
      const data: { timeRanges: Map<DayOfWeek, TimeRange[]>; availability: Map<DayOfWeek, boolean> } = {
        availability: dayEnabled,
        timeRanges: ranges,
      };
      const availability: DailyAvailability = { days: new Map() };
      dayEnabled.forEach((available, day) => {
        if (available) {
          availability.days.set(day, ranges.get(day));
        }
      });
      this.selectedAvailability.emit(availability);
      this.meetingSchedulerService.updateSchedule(data);
    }
  }

  get shouldShowSlotSelectionErrorValue(): boolean {
    return (
      this.isMultiHost &&
      (this.isAvailabilityFormOpened || this.displayAvailabilitySelectionError) &&
      !this.isAvailabilitySet
    );
  }

  get shouldShowSlotAvailabilityErrorValue(): boolean {
    if (
      this.shouldShowSlotSelectionErrorValue ||
      !this.isAvailabilityFormOpened ||
      !this.isAvailabilitySet ||
      this.displayAvailabilityInsufficientDurationError
    ) {
      return false;
    }
    return this.displayAvailabilitySelectionSlotError;
  }

  protected readonly indexToDayOfWeekMapping = indexToDayOfWeekMapping;
  protected readonly SETTINGS_VIEW = SETTINGS_VIEW;
}
